import { useState } from 'react'
import { Box } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'

import EnhancedTable from 'src/components/shared/EnhancedTable/EnhancedTable'
import { fieldsetTableStyles } from 'src/components/shared/styles/FieldsetTableStyles'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import { EditLxIcon } from 'src/components/shared/ui/Icons'
import LexnetDialog from 'src/components/shared/ui/LexnetDialog'
import LexnetIconButton from 'src/components/shared/ui/form/fields/LexnetIconButton'
import RemoveInfoButton from 'src/components/views/EscritosLexnet/components/shared/components/RemoveInfoButton'
import { FormField } from 'src/enums/Form'
import useNewModal from 'src/hooks/useModal'
import { RepresentanteLegal } from 'src/interfaces/EscritosLegales'
import { checkTipoPersonaFisica } from '../types'
import RepresentanteLegalForm from './RepresentanteLegalForm'

type SelectedRow = {
  index: number
  item: RepresentanteLegal | null
}
export default function RepresentanteLegalView({
  representantesData = []
}: {
  representantesData?: RepresentanteLegal[]
}): JSX.Element {
  const { setFieldValue } = useFormikContext()
  const { isOpened, close, open } = useNewModal()
  const [dataTable, setDataTable] = useState<RepresentanteLegal[]>(representantesData)
  const [selectedRow, setSelectedRow] = useState<SelectedRow>({ index: -1 } as SelectedRow)

  function deleteRow(_item: RepresentanteLegal, index: number) {
    if (index !== -1) {
      const data = [...dataTable.slice(0, index), ...dataTable.slice(index + 1)]
      setDataTable(data)
      setFieldValue(FormField.INTERVINIENTE_REPRESENTANTES_LEGALES, data)
    }
  }

  function editRow(item: RepresentanteLegal, index: number) {
    setSelectedRow({ index, item })
    open()
  }

  function editMode() {
    return selectedRow && selectedRow.index !== -1
  }

  const columns = [
    {
      Header: 'Representante Legal',
      accessor: (representante: RepresentanteLegal) => {
        return representante &&
          representante.personaTipo &&
          checkTipoPersonaFisica(representante.personaTipo)
          ? representante.nombre
          : `${representante.primerApellido}, ${representante.nombre}`
      }
    },
    {
      Header: 'Nº de identificación',
      accessor: 'identificacion'
    },
    {
      Header: ' ',
      accessor: (item: RepresentanteLegal, index: number) => (
        <Box className="cellActions">
          <LexnetIconButton
            handleClick={() => editRow(item, index)}
            aria-label={t('tooltips.edit')}
            title={t('tooltips.edit')}
          >
            <EditLxIcon />
          </LexnetIconButton>
          <RemoveInfoButton
            clear={() => deleteRow(item, index)}
            firstDialogMessage={t('dialog.message.remove_representante_legal')}
          />
        </Box>
      ),
      width: 100
    }
  ]

  function getRepresentante(values: RepresentanteLegal) {
    if (editMode()) {
      dataTable[selectedRow.index] = values
      setSelectedRow({ index: -1, item: null })
    }
    const newData = editMode() ? [...dataTable] : [...dataTable, values]
    setDataTable(newData)
    setFieldValue(FormField.INTERVINIENTE_REPRESENTANTES_LEGALES, newData)
  }

  function onCloseDialog() {
    setSelectedRow({ index: -1, item: null })
    close()
  }

  return (
    <>
      <Box sx={fieldsetTableStyles.wrapper}>
        <Box
          sx={fieldsetTableStyles.actions}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          <Box component="span" flex="1 1 auto" sx={fieldsetTableStyles.actionsInfo}>
            {t('form.legend.representantes_legales')}
          </Box>
          <ButtonPrimaryOutlined onClick={open}>{t('form.button.add')}</ButtonPrimaryOutlined>
        </Box>
        <EnhancedTable
          variation="form"
          columns={columns}
          data={dataTable && Object.keys(dataTable).length > 0 ? dataTable : []}
          toolbar={false}
          noRegistersMessage={t('components.enhanced_table.no_registers.representantes_legales')}
        />
      </Box>
      <LexnetDialog
        title={
          editMode()
            ? t('form.dialog.title.editar_representante_legal')
            : t('form.dialog.title.nuevo_representante_legal')
        }
        isOpen={isOpened}
        close={onCloseDialog}
      >
        <RepresentanteLegalForm
          closeDialog={onCloseDialog}
          getFormValues={getRepresentante}
          formValues={selectedRow.item}
        />
      </LexnetDialog>
    </>
  )
}
